import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TimelineModule } from 'primeng/timeline';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { TicketTimelineEntry, TimelineCommentData } from '../../../core/models/ticket.model';
import { TicketService } from '../../../core/services/ticket.service';
import { MarkdownModule } from 'ngx-markdown';

export type TimelineFilter =
  | 'all'
  | 'comments'
  | 'timeentries'
  | 'tasks'
  | 'attachments'
  | 'documentations'
  | 'opened'
  | 'public'
  | 'private'
  | 'history';

/**
 * Os filtros que o usuário marca e desmarca. `'all'` fica de fora de propósito: ele é um comando
 * ("marque todos"), não um item da seleção — seu estado aceso é derivado, vale quando todos os
 * demais estão marcados.
 */
export const SELECTABLE_FILTERS: readonly TimelineFilter[] = [
  'comments',
  'public',
  'private',
  'timeentries',
  'tasks',
  'attachments',
  'documentations',
  'opened',
  'history',
];

const EMPTY_MESSAGES: Partial<Record<TimelineFilter, string>> = {
  comments: 'Nenhum comentário encontrado',
  public: 'Nenhum comentário público encontrado',
  private: 'Nenhum comentário privado encontrado',
  timeentries: 'Nenhum apontamento encontrado',
  tasks: 'Nenhuma tarefa encontrada',
  attachments: 'Nenhum anexo encontrado',
  documentations: 'Nenhuma documentação encontrada',
  opened: 'Nenhuma abertura encontrada',
  history: 'Nenhuma movimentação registrada',
};

@Component({
  selector: 'app-ticket-timeline',
  standalone: true,
  imports: [
    CommonModule,
    TimelineModule,
    ButtonModule,
    TooltipModule,
    MarkdownModule,
  ],
  templateUrl: './ticket-timeline.component.html',
  styleUrls: ['./ticket-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketTimelineComponent implements OnInit, OnDestroy {
  private ticketService = inject(TicketService);
  private messageService = inject(MessageService);

  private readonly viewModeStorageKey = 'tsInfoDetailViewMode';
  private readonly filtersStorageKey = 'ticketTimelineFilters';

  @Input() set entries(value: TicketTimelineEntry[]) {
    this.entriesSignal.set(value);
  }

  @Input() highlightedEntryId: string | null = null;

  /**
   * Chips marcados. Começa com tudo marcado — a visão de sempre — até o usuário mexer: a partir daí
   * vale o que ficou salvo em localStorage, carregado no ngOnInit.
   */
  activeFilters = signal<Set<TimelineFilter>>(new Set(SELECTABLE_FILTERS));
  entriesSignal = signal<TicketTimelineEntry[]>([]);
  timelineViewMode = signal<'markdown' | 'text'>('markdown');
  private attachmentImageCache = new Map<string, string>();
  private loadingAttachments = new Set<string>();

  filteredEntries = computed(() => {
    const active = this.activeFilters();
    const allEntries = this.entriesSignal();

    if (active.size === SELECTABLE_FILTERS.length) return allEntries;
    if (active.size === 0) return [];

    // União dos filtros marcados: o card aparece se qualquer um deles o aceitar.
    return allEntries.filter(entry =>
      SELECTABLE_FILTERS.some(f => active.has(f) && this.matchesFilter(entry, f))
    );
  });

  /** Texto do estado vazio: específico quando há um único chip marcado, genérico no resto. */
  emptyMessage = computed(() => {
    const active = this.activeFilters();
    if (active.size === 0) return 'Nenhum filtro selecionado';
    if (active.size === SELECTABLE_FILTERS.length) return 'Nenhuma atividade registrada';
    if (active.size === 1) {
      const [only] = [...active];
      return EMPTY_MESSAGES[only] ?? 'Nenhuma atividade encontrada';
    }
    return 'Nenhuma atividade encontrada para os filtros selecionados';
  });

  private matchesFilter(entry: TicketTimelineEntry, filterType: TimelineFilter): boolean {
    switch (filterType) {
      case 'comments':
        return entry.type === 'comment';
      case 'timeentries':
        return entry.type === 'log' || entry.type === 'timeentry';
      case 'tasks':
        return entry.type === 'task';
      case 'attachments':
        return entry.type === 'attachment';
      case 'documentations':
        return entry.type === 'documentation';
      case 'opened':
        return entry.type === 'opened';
      case 'public':
        return entry.type === 'comment' && !!entry.isPublic;
      case 'private':
        return entry.type === 'comment' && !entry.isPublic;
      case 'history':
        return entry.type === 'history';
      default:
        return true;
    }
  }

  ngOnInit(): void {
    this.loadViewMode();
    this.loadFilters();
  }

  ngOnDestroy(): void {
    if (this._previewBlobUrl) {
      URL.revokeObjectURL(this._previewBlobUrl);
    }
  }

  /** Aceso quando o chip está marcado; para 'all', quando todos os outros estão. */
  isFilterActive(filterType: TimelineFilter): boolean {
    const active = this.activeFilters();
    if (filterType === 'all') return active.size === SELECTABLE_FILTERS.length;
    return active.has(filterType);
  }

  setFilter(filterType: TimelineFilter): void {
    const active = this.activeFilters();

    if (filterType === 'all') {
      // Com algo desmarcado, marca tudo; já com tudo marcado, o clique desfaz — o botão nunca fica
      // sem efeito.
      this.applyFilters(
        active.size === SELECTABLE_FILTERS.length ? new Set() : new Set(SELECTABLE_FILTERS)
      );
      return;
    }

    // Set novo a cada mudança: mutar o do signal não dispararia o computed com OnPush.
    const next = new Set(active);
    if (next.has(filterType)) {
      next.delete(filterType);
    } else {
      next.add(filterType);
    }
    this.applyFilters(next);
  }

  /** Único ponto de escrita do signal — a persistência mora aqui para nenhum caminho esquecê-la. */
  private applyFilters(next: Set<TimelineFilter>): void {
    this.activeFilters.set(next);
    this.persistFilters(next);
  }

  private persistFilters(filters: Set<TimelineFilter>): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(this.filtersStorageKey, JSON.stringify([...filters]));
  }

  /**
   * Restaura a seleção salva. Valor ausente, corrompido ou com chaves que não existem mais cai no
   * default (tudo marcado) — mas lista vazia é escolha legítima do usuário e é respeitada.
   */
  private loadFilters(): void {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(this.filtersStorageKey);
    if (stored === null) return;

    try {
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return;
      const valid = parsed.filter((f): f is TimelineFilter =>
        SELECTABLE_FILTERS.includes(f as TimelineFilter)
      );
      this.activeFilters.set(new Set(valid));
    } catch {
      // Valor inválido no storage não pode derrubar a timeline — segue com o default.
    }
  }

  toggleViewMode(): void {
    const next = this.timelineViewMode() === 'markdown' ? 'text' : 'markdown';
    this.timelineViewMode.set(next);
    this.persistViewMode(next);
  }

  private loadViewMode(): void {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(this.viewModeStorageKey) as 'markdown' | 'text' | null;
    if (stored === 'markdown' || stored === 'text') {
      this.timelineViewMode.set(stored);
    }
  }

  private persistViewMode(mode: 'markdown' | 'text'): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(this.viewModeStorageKey, mode);
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return (
      d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
      ' ' +
      d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    );
  }

  formatTime(dateStr: string | null | undefined): string {
    if (!dateStr) return '--:--';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  calcDuration(start: string | null | undefined, end: string | null | undefined): string {
    if (!start || !end) return '';
    const diff = new Date(end).getTime() - new Date(start).getTime();
    if (diff < 0) return '';
    const totalMin = Math.floor(diff / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return `${h}h${m > 0 ? m + 'min' : ''}`;
    return `${m}min`;
  }

  isEntryHighlighted(entry: TicketTimelineEntry): boolean {
    // Só o card de comentário: um mesmo ticket_details pode render também um card de movimentação,
    // e os dois carregam o mesmo `data.id` — sem esta guarda o destaque acenderia nos dois.
    if (entry.type !== 'comment' || !entry.data) return false;

    const isCommentData = (data: unknown): data is TimelineCommentData => {
      return typeof data === 'object' && data !== null && 'id' in data;
    };

    return isCommentData(entry.data) ? entry.data.id === this.highlightedEntryId : false;
  }

  getFileNameFromUrl(url: string | undefined | null | unknown): string {
    if (typeof url !== 'string' || !url) return 'Documento';
    const parts = url.split(/[/\\]/);
    const lastPart = parts[parts.length - 1];
    return lastPart || 'Documento';
  }

  // ── Preview / lightbox ─────────────────────────────────────────────────────

  previewVisible = signal(false);
  previewUrl = signal<string | null>(null);
  previewName = signal<string>('');
  previewLoading = signal(false);

  zoomScale = signal(1);
  panX = signal(0);
  panY = signal(0);
  dragging = signal(false);
  copyLinkLabel = signal('Copiar link');
  isInternalPreview = signal(false);

  imageTransform = computed(
    () => `translate(${this.panX()}px, ${this.panY()}px) scale(${this.zoomScale()})`
  );

  private _imageLinkForActions: string | null = null;
  // Blob criado exclusivamente para o preview (não pertence ao cache de thumbnails)
  private _previewBlobUrl: string | null = null;
  private _previewRequestId = 0;

  private dragActive = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private panStartX = 0;
  private panStartY = 0;
  private touchId: number | undefined = undefined;

  // ── Thumbnail loading ──────────────────────────────────────────────────────

  isImageUrl(fileName: string): boolean {
    if (!fileName) return false;
    const lower = fileName.toLowerCase();
    return (
      lower.endsWith('.jpg') ||
      lower.endsWith('.jpeg') ||
      lower.endsWith('.png') ||
      lower.endsWith('.gif') ||
      lower.endsWith('.webp') ||
      lower.endsWith('.svg')
    );
  }

  getAttachmentImageUrl(doc: any): string {
    if (!doc || !doc.link_file) return '';
    if (doc.link_file.startsWith('http')) return doc.link_file;
    return '';
  }

  loadAttachmentImageBlob(doc: any, imgElement: HTMLImageElement): void {
    if (!doc || !doc.link_file || !doc.link_file.startsWith('\\')) return;
    if (imgElement.src.startsWith('blob:') || imgElement.src.startsWith('data:')) return;

    const docId = doc.id;

    if (this.attachmentImageCache.has(docId)) {
      imgElement.src = this.attachmentImageCache.get(docId)!;
      return;
    }

    if (this.loadingAttachments.has(docId)) return;

    this.loadingAttachments.add(docId);

    this.ticketService.getAttachmentBlobTeoremaee(doc).subscribe({
      next: blob => {
        const objectUrl = URL.createObjectURL(blob);
        this.attachmentImageCache.set(docId, objectUrl);
        imgElement.src = objectUrl;
        this.loadingAttachments.delete(docId);
      },
      error: () => {
        const errorSvg =
          'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2260%22 height=%2260%22%3E%3Crect fill=%22%23f3f4f6%22 width=%2260%22 height=%2260%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22 dy=%22.3em%22 font-size=%2212%22 fill=%22%239ca3af%22%3E?%3C/text%3E%3C/svg%3E';
        this.attachmentImageCache.set(docId, errorSvg);
        imgElement.src = errorSvg;
        this.loadingAttachments.delete(docId);
      },
    });
  }

  // ── Lightbox actions ───────────────────────────────────────────────────────

  closePreview(): void {
    if (this._previewBlobUrl) {
      URL.revokeObjectURL(this._previewBlobUrl);
      this._previewBlobUrl = null;
    }
    this._previewRequestId++;
    this.previewVisible.set(false);
    this.previewUrl.set(null);
    this.previewName.set('');
    this.zoomScale.set(1);
    this.panX.set(0);
    this.panY.set(0);
    this.dragging.set(false);
    this.dragActive = false;
    this.copyLinkLabel.set('Copiar link');
    this._imageLinkForActions = null;
  }

  downloadAttachment(data: any, observation?: string): void {
    if (!data?.link_file) return;

    const fileName = this.getFileNameFromUrl(data.link_file);
    const isInternal = data.link_file.startsWith('\\');
    const isImage = this.isImageUrl(fileName);

    if (isImage) {
      const name = observation?.trim() || fileName;
      this.isInternalPreview.set(isInternal);
      this._imageLinkForActions = isInternal
        ? (data.ref ? `https://reverse.teorema.inf.br/teoremaee/api/chamadas/anexos/${data.ref}` : null)
        : (data.link_file ?? null);

      this.previewName.set(name);
      this.zoomScale.set(1);
      this.panX.set(0);
      this.panY.set(0);

      // Revogar blob exclusivo de preview anterior; blobs do cache de thumbnails não são tocados
      if (this._previewBlobUrl) {
        URL.revokeObjectURL(this._previewBlobUrl);
        this._previewBlobUrl = null;
      }

      if (isInternal) {
        // Reusar blob já carregado pela thumbnail — sem nova request
        const cached = this.attachmentImageCache.get(data.id);
        if (cached && cached.startsWith('blob:')) {
          this._previewRequestId++;
          this.previewUrl.set(cached);
          this.previewLoading.set(false);
          this.previewVisible.set(true);
          return;
        }

        const requestId = ++this._previewRequestId;
        this.previewUrl.set(null);
        this.previewLoading.set(true);
        this.previewVisible.set(true);

        this.ticketService.getAttachmentBlobTeoremaee(data).subscribe({
          next: blob => {
            // Descartar resposta obsoleta se o usuário fechou o preview
            if (requestId !== this._previewRequestId) return;
            const objectUrl = URL.createObjectURL(blob);
            this._previewBlobUrl = objectUrl;
            this.previewUrl.set(objectUrl);
            this.previewLoading.set(false);
          },
          error: () => {
            if (requestId !== this._previewRequestId) return;
            this.previewLoading.set(false);
            this.previewVisible.set(false);
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao carregar pré-visualização da imagem' });
          },
        });
      } else {
        this._previewRequestId++;
        this.previewUrl.set(data.link_file ?? null);
        this.previewLoading.set(false);
        this.previewVisible.set(true);
      }
    } else {
      if (isInternal) {
        this.ticketService.downloadAttachmentTeoremaee(data).subscribe({
          next: () =>
            this.messageService.add({ severity: 'success', summary: 'Download Iniciado', detail: 'Baixando anexo da rede interna' }),
          error: () =>
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao fazer download do anexo legado' }),
        });
      } else {
        window.open(data.link_file, '_blank');
      }
    }
  }

  openImagePreview(imageUrl: string, imageName?: string): void {
    if (!imageUrl) return;

    if (this._previewBlobUrl) {
      URL.revokeObjectURL(this._previewBlobUrl);
      this._previewBlobUrl = null;
    }
    this._previewRequestId++;

    this.previewName.set(imageName || this.getFileNameFromUrl(imageUrl));
    this._imageLinkForActions = imageUrl;
    this.isInternalPreview.set(false);
    this.zoomScale.set(1);
    this.panX.set(0);
    this.panY.set(0);
    this.previewUrl.set(imageUrl);
    this.previewLoading.set(false);
    this.previewVisible.set(true);
  }

  onPreviewImageError(): void {
    this.previewUrl.set(null);
    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar a imagem' });
  }

  openCurrentInNewTab(): void {
    const link = this._imageLinkForActions;
    if (link) window.open(link, '_blank');
  }

  copyCurrentLink(): void {
    const link = this._imageLinkForActions;
    if (!link) {
      this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'Link não disponível' });
      return;
    }
    navigator.clipboard.writeText(link).then(() => {
      this.copyLinkLabel.set('Copiado!');
      setTimeout(() => this.copyLinkLabel.set('Copiar link'), 2000);
    }).catch(() => {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar o link' });
    });
  }

  copyAttachmentLink(data: any, event: Event): void {
    event.stopPropagation();
    let url: string | undefined;

    if (data?.link_file?.startsWith('\\')) {
      if (data.ref) url = `https://reverse.teorema.inf.br/teoremaee/api/chamadas/anexos/${data.ref}`;
    } else {
      url = data?.link_file;
    }

    if (!url) {
      this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'Link não disponível' });
      return;
    }

    navigator.clipboard.writeText(url).then(() => {
      this.messageService.add({ severity: 'success', summary: 'Copiado!', detail: 'Link do anexo copiado para a área de transferência' });
    }).catch(() => {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar o link' });
    });
  }

  openDocumentation(data: any): void {
    if (data && data.slug) {
      window.open(`/teo-wiki/visualizar/${data.slug}`, '_blank');
    }
  }

  // ── Zoom ───────────────────────────────────────────────────────────────────

  zoomIn(): void { this._setZoom(this.zoomScale() * 1.4); }
  zoomOut(): void { this._setZoom(this.zoomScale() / 1.4); }
  zoomReset(): void { this._setZoom(1); }

  private _setZoom(scale: number): void {
    const clamped = Math.min(4, Math.max(0.25, scale));
    this.zoomScale.set(clamped);
    if (clamped <= 1) { this.panX.set(0); this.panY.set(0); }
  }

  // ── Mouse drag-to-pan ──────────────────────────────────────────────────────

  onDragStart(event: MouseEvent): void {
    if (event.button !== 0 || this.zoomScale() <= 1) return;
    event.preventDefault();
    this.dragActive = true;
    this.dragging.set(true);
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.panStartX = this.panX();
    this.panStartY = this.panY();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(event: MouseEvent): void {
    if (!this.dragActive) return;
    this.panX.set(this.panStartX + (event.clientX - this.dragStartX));
    this.panY.set(this.panStartY + (event.clientY - this.dragStartY));
  }

  @HostListener('document:mouseup')
  onDocMouseUp(): void {
    if (!this.dragActive) return;
    this.dragActive = false;
    this.dragging.set(false);
  }

  // ── Scroll-wheel zoom ──────────────────────────────────────────────────────

  onWheelZoom(event: WheelEvent): void {
    event.preventDefault();
    const factor = event.deltaY < 0 ? 1.12 : 0.88;
    const newScale = Math.min(4, Math.max(0.25, this.zoomScale() * factor));
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const mx = event.clientX - rect.left - rect.width / 2;
    const my = event.clientY - rect.top - rect.height / 2;
    const ratio = newScale / this.zoomScale();
    this.panX.set(mx - (mx - this.panX()) * ratio);
    this.panY.set(my - (my - this.panY()) * ratio);
    this.zoomScale.set(newScale);
    if (newScale <= 1) { this.panX.set(0); this.panY.set(0); }
  }

  // ── Touch drag-to-pan ──────────────────────────────────────────────────────

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1 || this.zoomScale() <= 1) return;
    const t = event.touches[0];
    this.touchId = t.identifier;
    this.dragStartX = t.clientX;
    this.dragStartY = t.clientY;
    this.panStartX = this.panX();
    this.panStartY = this.panY();
    this.dragActive = true;
    this.dragging.set(true);
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.dragActive) return;
    const t = Array.from(event.touches).find(x => x.identifier === this.touchId);
    if (t === undefined) return;
    event.preventDefault();
    this.panX.set(this.panStartX + (t.clientX - this.dragStartX));
    this.panY.set(this.panStartY + (t.clientY - this.dragStartY));
  }

  onTouchEnd(): void {
    this.dragActive = false;
    this.dragging.set(false);
    this.touchId = undefined;
  }

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.previewVisible()) return;
    // Não interceptar atalhos quando um campo de texto está focado
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    switch (event.key) {
      case 'Escape':
        this.closePreview();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '0':
        this.zoomReset();
        break;
      case 'ArrowLeft':
        if (this.zoomScale() > 1) { this.panX.update(x => x + 40); event.preventDefault(); }
        break;
      case 'ArrowRight':
        if (this.zoomScale() > 1) { this.panX.update(x => x - 40); event.preventDefault(); }
        break;
      case 'ArrowUp':
        if (this.zoomScale() > 1) { this.panY.update(y => y + 40); event.preventDefault(); }
        break;
      case 'ArrowDown':
        if (this.zoomScale() > 1) { this.panY.update(y => y - 40); event.preventDefault(); }
        break;
    }
  }
}
